import React from 'react';
import Layout from '../components/Layout';
import PageHero from '../components/PageHero';
import ContactCTA from '../components/ContactCTA';
import SEO from '../components/SEO';
import { combineSchemas, organizationSchema, serviceSchema, breadcrumbSchema } from '../lib/schema';
import { Check, Server, Globe, ShieldCheck, Mail } from 'lucide-react';

const plans = [
  {
    name: 'Starter Hosting',
    price: '$12',
    period: '/mo',
    features: ['1 Website', '10 GB SSD Storage', 'Free SSL Certificate', 'Daily Backups', 'Email Support'],
    highlight: false,
  },
  {
    name: 'Business Hosting',
    price: '$29',
    period: '/mo',
    features: ['Unlimited Websites', '50 GB SSD Storage', 'Free SSL & CDN', 'Daily Backups', 'Priority Support', 'Free Domain Year 1'],
    highlight: true,
  },
  {
    name: 'Enterprise Cloud',
    price: '$79',
    period: '/mo',
    features: ['Unlimited Websites', '200 GB NVMe Storage', 'Dedicated Resources', 'Hourly Backups', '24/7 Phone Support', 'Free Migration'],
    highlight: false,
  },
];

const feats = [
  { icon: Server, title: 'Lightning-fast Servers', desc: 'NVMe SSD storage and LiteSpeed servers across global data centers.' },
  { icon: Globe, title: 'Free Domain & DNS', desc: 'Register or transfer your domain with managed DNS at no extra cost.', id: 'domains' },
  { icon: ShieldCheck, title: 'Free SSL & Security', desc: 'Free Let\u2019s Encrypt SSL, malware scanning and DDoS protection.', id: 'ssl' },
  { icon: Mail, title: 'Email Hosting', desc: 'Professional email at your domain with webmail, mobile sync and spam filtering.' },
];

const HostingPage = () => (
  <Layout>
    <SEO
      title="Managed Web Hosting Services | Fast, Secure & Reliable | Website Symphony"
      description="Managed web hosting with 99.9% uptime, free SSL, daily backups, free CDN and 24/7 expert support. Plans from $12/mo. Free migration on Business & Enterprise plans."
      keywords="managed web hosting, business web hosting, WordPress hosting, secure web hosting, SSL certificate, domain hosting"
      canonical="/hosting"
      schema={combineSchemas(
        organizationSchema,
        serviceSchema('Managed Web Hosting', 'Fast, secure managed hosting with 99.9% uptime, free SSL, daily backups and 24/7 support.', '/hosting'),
        breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Hosting', path: '/hosting' }]),
      )}
    />
    <PageHero
      title="Web Hosting Services"
      subtitle="Fast, secure managed hosting backed by 99.9% uptime and 24/7 expert support."
      breadcrumb="Hosting"
    />

    <section className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {feats.map((f) => (
            <div key={f.title} id={f.id} className="text-center bg-[#F7F7F5] rounded-lg p-7 hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 mx-auto rounded-full bg-[#2C5F66] flex items-center justify-center mb-4">
                <f.icon className="text-white" size={28} />
              </div>
              <h3 className="text-[#2C5F66] font-bold mb-2">{f.title}</h3>
              <p className="text-gray-600 text-sm">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="py-20 bg-[#F7F7F5]">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-14">
          <h2 className="text-[#2C5F66] text-3xl md:text-4xl font-extrabold">Pick the plan that fits your business</h2>
          <p className="text-gray-600 mt-3">All plans include free SSL, daily backups and one-click setup.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-7">
          {plans.map((p) => (
            <div
              key={p.name}
              className={`rounded-xl p-8 transition-all ${
                p.highlight
                  ? 'bg-[#2C5F66] text-white shadow-2xl scale-[1.03]'
                  : 'bg-white text-[#2C5F66] shadow-md hover:shadow-xl'
              }`}
            >
              {p.highlight && (
                <div className="inline-block bg-[#D9A04A] text-white text-xs font-bold px-3 py-1 rounded-full mb-4">MOST POPULAR</div>
              )}
              <h3 className="text-2xl font-extrabold mb-2">{p.name}</h3>
              <div className="flex items-end gap-1 mb-6">
                <span className="text-5xl font-extrabold">{p.price}</span>
                <span className={`pb-2 ${p.highlight ? 'text-white/70' : 'text-gray-500'}`}>{p.period}</span>
              </div>
              <ul className="space-y-3 mb-8">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm">
                    <Check size={16} className={p.highlight ? 'text-[#D9A04A]' : 'text-[#2C5F66]'} />
                    <span className={p.highlight ? 'text-white/90' : 'text-gray-700'}>{f}</span>
                  </li>
                ))}
              </ul>
              <a
                href="/contact"
                className={`block text-center font-semibold py-3 rounded-md transition-colors ${
                  p.highlight ? 'bg-[#C68B30] hover:bg-[#B17A24] text-white' : 'bg-[#2C5F66] hover:bg-[#244e54] text-white'
                }`}
              >
                Get Started
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>

    <ContactCTA defaultService="Hosting" heading="Need help choosing a plan?" subheading="Our hosting specialists will recommend the right plan based on your traffic and tech stack." />
  </Layout>
);

export default HostingPage;
